import { NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";
import type { TabDef } from "@/hooks/useTabParam";

export interface LinkTabsProps {
  tabs: readonly TabDef[];
  activeTab: string;
  tabHref: (tab: string) => string;
  className?: string;
}

/**
 * A tab strip whose tabs are real `<a href>`s, so each one can be cmd/middle/right-clicked into a
 * new browser tab. Visually identical to the `underline` variant of `TabsList`/`TabsTrigger` it
 * replaces -- including on hover, which is the part that is easy to get wrong.
 *
 * **Hover is split per branch on purpose.** The Radix original marked the active tab with a
 * `data-[state=active]:` attribute variant, which *ties* in specificity with the unconditional
 * `hover:` classes and so resolves by source order -- the `segmented` variant in `tabs.tsx` needed
 * an explicit `data-[state=active]:hover:` compound to win reliably (W-2 of the AUT-256 fix round).
 * Here the active tab is a bare conditional class with no modifier, which is *lower* specificity
 * than `hover:text-foreground`/`hover:border-border` and would provably lose on hover. So only the
 * inactive branch carries those hovers, and the active branch pins its own colour rather than
 * leaving it unset. Do not merge the two branches into one shared hover rule.
 *
 * Pair with `useTabParam`, which supplies `activeTab` and `tabHref`.
 */
export function LinkTabs({ tabs, activeTab, tabHref, className }: LinkTabsProps) {
  return (
    <div
      className={cn(
        "justify-start gap-0 bg-transparent h-auto p-0 border-b border-border w-full rounded-none inline-flex items-center text-muted-foreground",
        className,
      )}
    >
      {tabs.map((t) => {
        const Icon = t.icon;

        return (
          <NavLink
            key={t.id}
            to={tabHref(t.id)}
            className={cn(
              "inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              "gap-1.5 px-4 py-2.5 border-b-2 rounded-none -mb-px",
              activeTab === t.id
                ? "border-primary text-primary bg-transparent shadow-none hover:border-primary hover:text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground hover:border-border",
            )}
          >
            {Icon && <Icon className="h-4 w-4" />}
            {t.label}
          </NavLink>
        );
      })}
    </div>
  );
}
